import { useId, useRef, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { PASSWORD_HINT } from '../../../core/rules/password';
import { Spinner } from '../../../ui/Spinner';
import { btnPrimary, btnQuiet } from '../../../ui/styles';
import { FormError } from '../../shared/FormError';
import { PasswordInput } from '../PasswordInput';
type Errors = {
  currentPassword?: string;
  password?: string;
};

/**
 * Wachtwoord in Gegevens. Wie alleen met inloglinks binnenkwam, stelt er één in (de server vraagt
 * dan een recente login); wie er al een heeft, wijzigt het met het huidige wachtwoord erbij.
 * Na opslaan logt de server de andere apparaten uit.
 */
export function PasswordSection() {
  const {
    account,
    changePassword,
    signOut
  } = useSignedInAccount();
  const has = account.customer.hasPassword;
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [error, setError] = useState<string | null>(null);
  const [reauth, setReauth] = useState(false);
  const [saving, setSaving] = useState(false);
  const currentRef = useRef<HTMLInputElement>(null);
  const nextRef = useRef<HTMLInputElement>(null);
  const id = useId();
  const title = has ? 'Wachtwoord wijzigen' : 'Wachtwoord instellen';
  const close = () => {
    setOpen(false);
    setCurrent('');
    setNext('');
    setErrors({});
    setError(null);
    setReauth(false);
  };
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setErrors({});
    setError(null);
    setReauth(false);
    const r = await changePassword(has ? {
      currentPassword: current,
      password: next
    } : {
      password: next
    });
    setSaving(false);
    if (r.ok) {
      close();
      toast.success('Opgeslagen. Je bent op je andere apparaten uitgelogd.');
      return;
    }
    if (r.code === 'reauth_required') {
      setReauth(true);
      setError(r.error);
      return;
    }
    const found: Errors = {
      currentPassword: r.fields?.currentPassword,
      password: r.fields?.password
    };
    if (found.currentPassword || found.password) {
      setErrors(found);
      // Focus naar het eerste veld met een fout, in de volgorde van het formulier.
      const firstBad = found.currentPassword ? currentRef : nextRef;
      firstBad.current?.focus();
    } else setError(r.error);
  };
  return <section aria-labelledby={`${id}-title`} className="mt-10" data-id="element-6596" data-mp="j3bjidgb5159">
      <h2 id={`${id}-title`} className="font-display text-[24px] font-light text-ink" data-id="element-6597" data-mp="e3p35v56n4s8">
        {title}
      </h2>
      <div className="mt-4 border-y border-line py-4" data-id="element-6598" data-mp="s3ge1n0ek520">
        {open ? <form onSubmit={e => void save(e)} noValidate aria-labelledby={`${id}-title`} className="max-w-sm space-y-4" data-id="element-6599" data-mp="n20wi4xfogod">
            {/* Voor wachtwoordmanagers: zo bewaren ze het nieuwe wachtwoord bij het juiste account. */}
            <input type="email" name="username" autoComplete="username" value={account.customer.email} readOnly hidden data-id="element-6600" data-mp="1yhz4s6kseyoj" />
            {!has && <p className="text-[14px] leading-relaxed text-muted" data-id="element-6601" data-mp="2b1cot97isxvw">
                Je logt nu in met een link in je mail. Met een wachtwoord kan het ook direct, maar de link blijft gewoon werken.
              </p>}
            {has && <PasswordInput id={`${id}-current`} label="Huidig wachtwoord" value={current} onChange={v => {
          setCurrent(v);
          if (errors.currentPassword) setErrors(x => ({
            ...x,
            currentPassword: undefined
          }));
        }} autoComplete="current-password" error={errors.currentPassword} inputRef={currentRef} data-id="element-6602" data-mp="220er18nodmlr" />}
            <PasswordInput id={`${id}-new`} label="Nieuw wachtwoord" value={next} onChange={v => {
          setNext(v);
          if (errors.password) setErrors(x => ({
            ...x,
            password: undefined
          }));
        }} autoComplete="new-password" hint={PASSWORD_HINT} error={errors.password} inputRef={nextRef} data-id="element-6603" data-mp="38nabzjwuhre6" />
            {reauth ? <div role="alert" className="border border-clay/40 bg-blush px-4 py-3 text-[14px] leading-relaxed text-ink" data-id="element-6604" data-mp="1aur9jpdsem6k">
                <p data-id="element-6605" data-mp="28goojcsyk9dr">{error}</p>
                <button type="button" onClick={signOut} className={`${btnPrimary} mt-3`} data-id="element-6606" data-mp="23ierewnoaypi">
                  Opnieuw inloggen
                </button>
              </div> : <FormError message={error} data-id="element-6607" data-mp="1ypbvwizc5f6o" />}
            <div className="flex flex-wrap gap-2" data-id="element-6608" data-mp="1trk37mqv9p1f">
              <button type="submit" disabled={saving} className={btnPrimary} data-id="element-6609" data-mp="2r7zcbl3e061f">
                {saving && <Spinner data-id="element-6610" data-mp="2r9smvbh4faep" />}
                {saving ? 'Bezig…' : 'Opslaan'}
              </button>
              <button type="button" onClick={close} className={btnQuiet} data-id="element-6611" data-mp="2mfg0oto9fkz9">
                Annuleren
              </button>
            </div>
          </form> : <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-6612" data-mp="2ys0igsh888ic">
            <div className="min-w-0" data-id="element-6613" data-mp="33pjwbra0ij9r">
              <p className="text-[15px] text-ink" data-id="element-6614" data-mp="33so8batpyjaz">{has ? 'Je logt in met een wachtwoord' : 'Je logt in met een link in je mail'}</p>
              <p className="mt-0.5 text-[13px] text-muted" data-id="element-6615" data-mp="3crjckhd7djbn">
                {has ? 'Na het wijzigen log je op je andere apparaten opnieuw in.' : 'Stel een wachtwoord in als je liever direct inlogt.'}
              </p>
            </div>
            <button type="button" onClick={() => setOpen(true)} aria-label={title} className={`${btnQuiet} shrink-0`} data-id="element-6616" data-mp="3mie2pwtkl32d">
              {has ? 'Wijzig' : 'Instellen'}
            </button>
          </div>}
      </div>
    </section>;
}